/**
 * ดาบผลักผาชาด (cliff-push-sabre): its lock routine and its own ท่าใหญ่ (design docs/design/combat/2026-10-11-lock-moves-13.md
 * §2.9, kit audit docs/design/combat/2026-10-13-art-kit-audit.md piece 10). Re-themed (user Q2, DECISIONS 2026-10-13): push /
 * ring-out instead of bleed — the art takes ground: its cuts drive the foe back even through a guard (data/duelPush.ts), the
 * lock routine marches it 3 m toward the ring line, and the ult walks the whole field back to the edge (long moves never carry
 * a foe past ring radius − `LONG_ULT.ringMargin`: the ring-out is left to the next cut). Blood-sabre keeps bleed + lifesteal.
 *
 * Lock move rc-edge 鋒 (range 2.2, the 0.35 s lock wind-up: sabre hoisted overhead in both hands, front foot stamped — a dust
 * puff — the edge reddens 1 px). The caster presses in after every chop (the routine's band), so the march reads as steps.
 * Keys (rc* poses, chop/cliff fx, stamp/chop/rock sfx, lower and heavier each chop) are the contract for the motion / FX /
 * sound lanes; AI 1–2 per step (a heavy body, few copies). Data only.
 */
import { registerLongMove } from './registry';

/** 鋒 คมไล่ผาห้าก้าว — four chops each a step forward (0.4 m back each), the sabre planted, the cleave up and out. */
export const FIVE_STEP_CLIFF = registerLongMove({
  skillId: 'rc-edge',
  kind: 'lock',
  name: 'คมไล่ผาห้าก้าว',
  opener: { type: 'contact', range: 0, tell: 0 },
  len: 5.6,
  catch: 0.5,
  power: 2.6,
  lunge: 1.0,
  beats: [
    { at: 0, pose: 'rcBrace', hits: 0, share: 0, foe: 'hit', fx: 'cliffDust', sfx: 'stampHeavy', cam: 'hold' },
    { at: 0.5, pose: 'rcChop1', hits: 1, share: 0.1, foe: 'knock', dist: 0.4, ai: 1, fx: 'chopArc1', sfx: 'sabreChop' },
    { at: 1.2, pose: 'rcChop2', hits: 1, share: 0.12, foe: 'knock', dist: 0.4, ai: 1, fx: 'chopArc2', sfx: 'sabreChop2' },
    { at: 1.85, pose: 'rcChop3', hits: 1, share: 0.14, foe: 'knock', dist: 0.4, ai: 2, fx: 'chopArc3', sfx: 'sabreChop3' },
    { at: 2.45, pose: 'rcChop4', hits: 1, share: 0.14, foe: 'knock', dist: 0.4, ai: 2, fx: 'chopArc4', sfx: 'sabreChop4' },
    { at: 3.05, pose: 'rcPlant', hits: 0, share: 0, foe: 'kneel', fx: 'groundCrack', sfx: 'rockCrack', cam: 'push' },
    {
      at: 3.75,
      pose: 'rcCleaveUp',
      hits: 1,
      share: 0.5,
      foe: 'knock',
      dist: 1.4,
      stop: 0.14,
      fx: 'cliffSplit',
      sfx: 'rockBurst',
      cam: 'shake',
    },
    { at: 4.35, pose: 'rcFollow', hits: 0, share: 0, foe: 'fall' },
    { at: 5.0, pose: 'rcStance', hits: 0, share: 0, foe: 'recover' },
  ],
});

/**
 * 海 ผาแดงถล่มทะเล — the ท่าใหญ่: a field (3.6 m), three marching chops that each step in and drive every caught foe back,
 * the sabre heaved up like a cliff face, then brought down on them all (3.0 m back in total = `LONG_ULT.maxKnock`). Not a spin,
 * no lifesteal, no bleed: that is heat-sabre's bs-massacre.
 */
export const CLIFF_INTO_SEA = registerLongMove({
  skillId: 'rc-sea',
  kind: 'ult',
  name: 'ผาแดงถล่มทะเล',
  opener: {
    type: 'field',
    range: 0,
    radius: 3.6,
    tell: 0.8,
    tellPose: 'rcSeaHoist',
    tellFx: 'cliffRing',
    tellSfx: 'rcRageFlare',
  },
  len: 4.7,
  power: 3.8,
  lunge: 0,
  whiff: { pose: 'rcSeaSpent', fx: 'cliffDustScatter', sfx: 'rcSpent', stop: 0.9, rageBack: 10 },
  beats: [
    { at: 0, pose: 'rcSeaStamp', hits: 1, share: 0.05, foe: 'hit', fx: 'cliffDust', sfx: 'seaStamp', cam: 'pull', stop: 0.05 },
    {
      at: 0.45,
      pose: 'rcSeaChop1',
      hits: 1,
      share: 0.1,
      foe: 'knock',
      dist: 0.5,
      move: { kind: 'pass', dist: 0.6 },
      ai: 1,
      fx: 'seaChopArc',
      sfx: 'seaChop1',
      stop: 0.06,
    },
    {
      at: 1.05,
      pose: 'rcSeaChop2',
      hits: 1,
      share: 0.1,
      foe: 'knock',
      dist: 0.5,
      move: { kind: 'pass', dist: 0.6 },
      ai: 1,
      fx: 'seaChopArc',
      sfx: 'seaChop2',
      stop: 0.06,
    },
    {
      at: 1.65,
      pose: 'rcSeaChop3',
      hits: 1,
      share: 0.12,
      foe: 'knock',
      dist: 0.5,
      move: { kind: 'pass', dist: 0.6 },
      ai: 2,
      fx: 'seaChopArc',
      sfx: 'seaChop3',
      cam: 'orbit',
      stop: 0.06,
    },
    { at: 2.25, pose: 'rcSeaHeave', hits: 0, share: 0, foe: 'kneel', fx: 'cliffRise', sfx: 'cliffGroan', cam: 'push' },
    {
      at: 2.85,
      pose: 'rcSeaCollapse',
      hits: 2,
      share: 0.63,
      foe: 'knock',
      dist: 1.5,
      ai: 2,
      fx: 'cliffCollapse',
      sfx: 'cliffAvalanche',
      cam: 'shake',
      stop: 0.18,
    },
    { at: 3.6, pose: 'rcSeaPlant', hits: 0, share: 0, foe: 'fall', fx: 'rockSettle', sfx: 'sabreRingDown' },
    { at: 4.2, pose: 'rcSeaStance', hits: 0, share: 0, foe: 'recover', fx: 'cliffDustFade', sfx: 'rcExhale' },
  ],
});
